import { createId } from "@paralleldrive/cuid2"
import {
  bigint,
  doublePrecision,
  foreignKey,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
} from "drizzle-orm/pg-core"
import { projectsTable, stores } from "./app-schema"
import { configurationVersions } from "./store-configuration-schema"

// =============================================================================
// Evaluation Benchmark & Run tables
// =============================================================================

export const evaluationBenchmarks = pgTable(
  "evaluation_benchmarks",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    projectId: text("project_id"),
    storeId: text("store_id"),
    name: text().notNull(),
    groundTruthData: jsonb("ground_truth_data").$type<Record<string, {}>>().notNull(),
    configVersionId: text("config_version_id"),
    imageData: text("image_data"), // base64 data URL of the store floor plan image
    snapshotedAt: timestamp("snapshoted_at", { withTimezone: true }).defaultNow().notNull(),
    notes: text(),
  },
  (table) => [
    foreignKey({
      columns: [table.projectId],
      foreignColumns: [projectsTable.id],
      name: "evaluation_benchmarks_project_id_project_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.storeId],
      foreignColumns: [stores.id],
      name: "evaluation_benchmarks_store_id_stores_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "evaluation_benchmarks_config_version_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    index("evaluation_benchmarks_project_id_idx").on(table.projectId),
    index("evaluation_benchmarks_store_id_idx").on(table.storeId),
  ],
)

export const evaluationBatches = pgTable(
  "evaluation_batches",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    configSnapshot: jsonb("config_snapshot").$type<Record<string, {}>>().notNull(),
    source: text().notNull(), // 'nightly' | 'manual'
    startedAt: timestamp("started_at", { withTimezone: true }).defaultNow().notNull(),
    finishedAt: timestamp("finished_at", { withTimezone: true }),
    totalBenchmarks: integer("total_benchmarks").notNull().default(0),
    correctMeters: integer("correct_meters").notNull().default(0),
    totalMeters: integer("total_meters").notNull().default(0),
    overallMeterAccuracy: doublePrecision("overall_meter_accuracy").notNull().default(0),
    notes: text(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "evaluation_batches_config_version_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
    index("evaluation_batches_source_finished_at_idx").on(table.source, table.finishedAt),
    index("evaluation_batches_started_at_idx").on(table.startedAt),
  ],
)

export const evaluationRuns = pgTable(
  "evaluation_runs",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    batchId: text("batch_id").notNull(),
    benchmarkId: text("benchmark_id").notNull(),
    configVersionId: text("config_version_id").notNull(),
    configLocale: text("config_locale"),
    optimizerMode: text("optimizer_mode").notNull().default("humanLike"),
    generatedAssignments: jsonb("generated_assignments").$type<Record<string, {}>[]>().notNull(), // SchemaRowAssignment[]
    metrics: jsonb().$type<Record<string, {}>>().notNull(),
    durationMs: bigint("duration_ms", { mode: "number" }).notNull(),
    runAt: timestamp("run_at", { withTimezone: true }).defaultNow().notNull(),
    notes: text(),
  },
  (table) => [
    foreignKey({
      columns: [table.batchId],
      foreignColumns: [evaluationBatches.id],
      name: "evaluation_runs_batch_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.benchmarkId],
      foreignColumns: [evaluationBenchmarks.id],
      name: "evaluation_runs_benchmark_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "evaluation_runs_config_version_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
    index("evaluation_runs_batch_id_idx").on(table.batchId),
    index("evaluation_runs_benchmark_id_idx").on(table.benchmarkId),
    index("evaluation_runs_config_version_id_idx").on(table.configVersionId),
    index("evaluation_runs_run_at_idx").on(table.runAt),
  ],
)
